import { useState, useEffect, useRef } from "react";
import Logo from "./components/Logo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getUserProfile } from "../utils/api";

const Header = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [user, setUser] = useState(null);
	const dropdownRef = useRef(null);
	const { pathname, hash } = useLocation();
	const navigate = useNavigate();
	const authentication = localStorage.getItem("token");

	// Pages with dark gradient backgrounds that need transparent header
	const darkBackgroundPages = [
		"/",
		"/subscription",
		"/contact_us",
		"/login",
		"/register",
		"/manager/register",
		"/profile",
		"/form_page",
		"/find_car",
		"/find_salvageCar",
		"/generatePage",
		"/compareCars",
		"/compareSalvage",
		"/product_page",
		"/privacy",
		"/terms",
	];
	const isDarkPage = darkBackgroundPages.some((page) =>
		page === "/" ? pathname === "/" : pathname.startsWith(page),
	);

	// Fetch user profile when authenticated
	useEffect(() => {
		const fetchUserProfile = async () => {
			const res = await getUserProfile();
			if (res?.data?.statusCode === 200) {
				setUser(res.data.data);
			}
		};

		if (authentication) {
			fetchUserProfile();
		}
	}, [authentication]);

	// Handle hash scrolling after navigation
	useEffect(() => {
		if (hash) {
			const element = document.querySelector(hash);
			if (element) {
				setTimeout(() => {
					element.scrollIntoView({ behavior: "smooth" });
				}, 100);
			}
		}
	}, [hash]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	// Handle navigation link clicks
	const handleNavClick = (e, href) => {
		if (href.includes("#")) {
			const [path, hash] = href.split("#");
			const targetPath = path || "/";

			if (pathname === targetPath) {
				// Already on the page, just scroll
				e.preventDefault();
				const element = document.getElementById(hash);
				if (element) {
					element.scrollIntoView({ behavior: "smooth" });
				}
			}
			// Otherwise, let the Link navigate and the useEffect will handle scrolling
		}
	};

	const handleLogout = () => {
		localStorage.clear();
		setUser(null);
		setDropdownOpen(false);
		navigate("/");
	};

	const navLinks = [
		{ label: "Home", href: "/" },
		{ label: "Features", href: "/#features" },
		{ label: "Pricing", href: "/subscription" },
		{ label: "Contact", href: "/contact_us" },
	];

	// On dark background pages: transparent header
	// On other pages: fixed white header
	const showWhiteHeader = !isDarkPage;

	const getUserInitials = () => {
		if (user?.name) {
			const names = user.name.split(" ");
			if (names.length >= 2) {
				return `${names[0][0]}${names[1][0]}`.toUpperCase();
			}
			return user.name.substring(0, 2).toUpperCase();
		}
		return "U";
	};

	return (
		<header
			className={`absolute top-0 left-0 right-0 z-50 transition-all duration-300 ${
				showWhiteHeader ? "bg-white shadow-sm" : "bg-transparent"
			}`}
		>
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex justify-between items-center h-20">
					{/* Logo */}
					<button
						type="button"
						className="cursor-pointer flex items-center bg-transparent border-0 p-0"
						onClick={() => navigate("/")}
					>
						<Logo variant={showWhiteHeader ? "default" : "light"} size="md" />
					</button>

					{/* Desktop Navigation - Only show when not logged in */}
					{!authentication && (
						<nav className="hidden md:flex items-center gap-8">
							{navLinks.map((link) => (
								<Link
									key={link.href}
									to={link.href}
									onClick={(e) => handleNavClick(e, link.href)}
									className={`font-medium transition-colors ${
										showWhiteHeader
											? "text-gray-700 hover:text-blue-600"
											: "text-white hover:text-white/80"
									}`}
								>
									{link.label}
								</Link>
							))}
						</nav>
					)}

					{/* Auth Buttons - Desktop */}
					<div className="hidden md:flex items-center gap-4">
						{!authentication ? (
							<>
								<button
									type="button"
									onClick={() => navigate("/login")}
									className={`font-medium transition-colors ${
										showWhiteHeader
											? "text-gray-700 hover:text-blue-600"
											: "text-white hover:text-white/80"
									}`}
								>
									Sign in
								</button>
								<button
									type="button"
									onClick={() => navigate("/register")}
									className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
								>
									Get Started
								</button>
							</>
						) : (
							<div className="flex items-center gap-4">
								<button
									type="button"
									onClick={() =>
										navigate(
											user?.role === "manager" ? "/subscription" : "/form_page",
										)
									}
									className={`font-medium transition-colors ${
										showWhiteHeader
											? "text-gray-700 hover:text-blue-600"
											: "text-white hover:text-white/80"
									}`}
								>
									{user?.role === "manager" ? "Account" : "Dashboard"}
								</button>

								{/* User Dropdown */}
								<div className="relative" ref={dropdownRef}>
									<button
										type="button"
										onClick={() => setDropdownOpen(!dropdownOpen)}
										className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
											showWhiteHeader
												? "hover:bg-gray-100"
												: "hover:bg-white/10"
										}`}
									>
										{/* Avatar */}
										<div className="w-9 h-9 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center border-2 border-cyan-500">
											<span className="text-white text-sm font-semibold">
												{getUserInitials()}
											</span>
										</div>
										{/* Dropdown Arrow */}
										<svg
											className={`w-4 h-4 transition-transform ${dropdownOpen ? "rotate-180" : ""} ${
												showWhiteHeader ? "text-gray-600" : "text-white"
											}`}
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
											aria-hidden="true"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M19 9l-7 7-7-7"
											/>
										</svg>
									</button>

									{/* Dropdown Menu */}
									{dropdownOpen && (
										<div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg py-2 border border-gray-100 z-50">
											{/* User Info */}
											<div className="px-4 py-3 border-b border-gray-100">
												<p className="text-sm font-semibold text-gray-900 truncate">
													{user?.name || "User"}
												</p>
												<p className="text-xs text-gray-500 truncate">
													{user?.email || ""}
												</p>
											</div>

											{/* Menu Items */}
											<div className="py-2">
												<button
													type="button"
													onClick={() => {
														setDropdownOpen(false);
														navigate("/profile");
													}}
													className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
												>
													<svg
														className="w-5 h-5 text-gray-400"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
														aria-hidden="true"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
														/>
													</svg>
													My Profile
												</button>

												{user?.role !== "manager" && (
													<button
														type="button"
														onClick={() => {
															setDropdownOpen(false);
															navigate("/form_page");
														}}
														className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
													>
														<svg
															className="w-5 h-5 text-gray-400"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
															aria-hidden="true"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
															/>
														</svg>
														Dashboard
													</button>
												)}

												{true && (
													<button
														type="button"
														onClick={() => {
															setDropdownOpen(false);
															navigate("/subscription");
														}}
														className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
													>
														<svg
															className="w-5 h-5 text-gray-400"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
															aria-hidden="true"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
															/>
														</svg>
														Subscription
													</button>
												)}
											</div>

											{/* Logout */}
											<div className="border-t border-gray-100 pt-2">
												<button
													type="button"
													onClick={handleLogout}
													className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
												>
													<svg
														className="w-5 h-5"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
														aria-hidden="true"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
														/>
													</svg>
													Sign out
												</button>
											</div>
										</div>
									)}
								</div>
							</div>
						)}
					</div>

					{/* Mobile Menu Button */}
					<button
						type="button"
						onClick={() => setIsOpen(!isOpen)}
						className={`md:hidden p-2 rounded-lg transition-colors ${
							showWhiteHeader
								? "text-gray-700 hover:bg-gray-100"
								: "text-white hover:bg-white/10"
						}`}
						aria-label="Toggle menu"
					>
						{isOpen ? (
							<svg
								className="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						) : (
							<svg
								className="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 6h16M4 12h16M4 18h16"
								/>
							</svg>
						)}
					</button>
				</div>

				{/* Mobile Menu */}
				<div
					className={`md:hidden overflow-hidden transition-all duration-300 ${
						isOpen ? "max-h-[32rem] pb-6" : "max-h-0"
					}`}
				>
					<div className="bg-white rounded-2xl shadow-lg p-4 mt-2">
						{/* User Info for Mobile (when logged in) */}
						{authentication && user && (
							<div className="flex items-center gap-3 px-4 py-3 mb-2 bg-gray-50 rounded-lg">
								<div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0 border-2 border-cyan-500">
									<span className="text-white text-sm font-semibold">
										{getUserInitials()}
									</span>
								</div>
								<div className="min-w-0">
									<p className="text-sm font-semibold text-gray-900 truncate">
										{user.name}
									</p>
									<p className="text-xs text-gray-500 truncate">{user.email}</p>
								</div>
							</div>
						)}

						{/* Navigation links - Only show when not logged in */}
						{!authentication && (
							<nav className="space-y-1">
								{navLinks.map((link) => (
									<Link
										key={link.href}
										to={link.href}
										onClick={(e) => {
											handleNavClick(e, link.href);
											setIsOpen(false);
										}}
										className="block px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
									>
										{link.label}
									</Link>
								))}
							</nav>
						)}
						<div
							className={`${!authentication ? "border-t border-gray-100 mt-4 pt-4" : ""} space-y-1`}
						>
							{!authentication ? (
								<>
									<button
										type="button"
										onClick={() => {
											navigate("/login");
											setIsOpen(false);
										}}
										className="w-full px-4 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-left"
									>
										Sign in
									</button>
									<button
										type="button"
										onClick={() => {
											navigate("/register");
											setIsOpen(false);
										}}
										className="w-full px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all text-center"
									>
										Get Started
									</button>
								</>
							) : (
								<>
									<button
										type="button"
										onClick={() => {
											navigate("/profile");
											setIsOpen(false);
										}}
										className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
									>
										<svg
											className="w-5 h-5 text-gray-400"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
											aria-hidden="true"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
											/>
										</svg>
										My Profile
									</button>
									{user?.role !== "manager" && (
										<button
											type="button"
											onClick={() => {
												navigate("/form_page");
												setIsOpen(false);
											}}
											className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
										>
											<svg
												className="w-5 h-5 text-gray-400"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
												aria-hidden="true"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
												/>
											</svg>
											Dashboard
										</button>
									)}
									{true && (
										<button
											type="button"
											onClick={() => {
												navigate("/subscription");
												setIsOpen(false);
											}}
											className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
										>
											<svg
												className="w-5 h-5 text-gray-400"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
												aria-hidden="true"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
												/>
											</svg>
											Subscription
										</button>
									)}
									<div className="border-t border-gray-100 mt-2 pt-2">
										<button
											type="button"
											onClick={() => {
												handleLogout();
												setIsOpen(false);
											}}
											className="w-full flex items-center gap-3 px-4 py-3 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors"
										>
											<svg
												className="w-5 h-5"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
												aria-hidden="true"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
												/>
											</svg>
											Sign out
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</header>
	);
};

export default Header;
